'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/switcher-palette/styles.css';
import { type ReactNode, useEffect, useRef } from 'react';

export type SwitcherPaletteProps = {
  children: ReactNode;
  label: string;
  onClose: () => void;
  onQueryChange: (query: string) => void;
  open: boolean;
  query: string;
  searchAction?: ReactNode;
  searchTestId: string;
  testId: string;
};

/**
 * A search-first switcher over every entity a person can act as, opened from
 * the rail head. Each app owns the data, the matching and what choosing does;
 * this owns the native dialog, which gives the focus trap, Escape and the
 * backdrop for free, where a hand-rolled overlay has to reimplement them and
 * usually gets them wrong for a keyboard.
 */
export const SwitcherPalette = ({ children, label, onClose, onQueryChange, open, query, searchAction, searchTestId, testId }: SwitcherPaletteProps): ReactNode => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      onQueryChange('');
      dialog.showModal();
      inputRef.current?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [onQueryChange, open]);

  /* Bound to the element rather than written as a JSX handler: a native dialog
     fills the viewport, so a click that lands on the dialog element itself
     landed outside its box. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const backdrop = (event: MouseEvent) => {
      if (event.target === dialog) onClose();
    };
    dialog.addEventListener('click', backdrop);
    return () => dialog.removeEventListener('click', backdrop);
  }, [onClose]);

  return (
    <dialog
      aria-label={label}
      className='bomdisoft-switcher-palette'
      data-testid={testId}
      onCancel={event => {
        event.preventDefault();
        onClose();
      }}
      ref={dialogRef}
    >
      <div className='bomdisoft-switcher-palette-inner'>
        <div className='bomdisoft-switcher-palette-search-row' data-action={searchAction === undefined ? undefined : ''} data-testid={`${searchTestId}-row`}>
          <input aria-label={label} className='bomdisoft-switcher-palette-search' data-testid={searchTestId} onChange={event => onQueryChange(event.target.value)} placeholder={label} ref={inputRef} value={query} />
          {searchAction}
        </div>
        {children}
      </div>
    </dialog>
  );
};

export type SwitcherPaletteGroupProps = { children: ReactNode; label: ReactNode };

export const SwitcherPaletteGroup = ({ children, label }: SwitcherPaletteGroupProps): ReactNode => (
  <>
    <p className='bomdisoft-switcher-palette-group'>{label}</p>
    {children}
  </>
);

export type SwitcherPaletteRowProps = {
  actions?: ReactNode;
  chooseTestId: string;
  isCurrent?: boolean;
  isLocked?: boolean;
  lockedLabel?: string;
  mark: ReactNode;
  meta?: ReactNode;
  name: ReactNode;
  onChoose: () => void;
  testId?: string;
};

/** The choose button holds the mark, the name and any meta; `actions` sit
 *  outside it because buttons cannot nest. */
export const SwitcherPaletteRow = ({ actions, chooseTestId, isCurrent, isLocked, lockedLabel, mark, meta, name, onChoose, testId }: SwitcherPaletteRowProps): ReactNode => (
  <div className='bomdisoft-switcher-palette-row' data-current={isCurrent === true ? '' : undefined} data-locked={isLocked === true ? '' : undefined} data-testid={testId}>
    <button aria-label={isLocked === true ? lockedLabel : undefined} className='bomdisoft-switcher-palette-choose' data-testid={chooseTestId} onClick={onChoose} type='button'>
      {mark}
      <span className='bomdisoft-switcher-palette-name'>{name}</span>
      {meta}
      {isLocked === true ? <LockGlyph /> : null}
    </button>
    {actions === undefined ? null : <span className='bomdisoft-switcher-palette-actions'>{actions}</span>}
  </div>
);

export type SwitcherPaletteFailedProps = {
  message: ReactNode;
  onRetry: () => void;
  reason?: string;
  reasonTestId: string;
  retryLabel: ReactNode;
  retryTestId: string;
  testId: string;
};

export const SwitcherPaletteFailed = ({ message, onRetry, reason, reasonTestId, retryLabel, retryTestId, testId }: SwitcherPaletteFailedProps): ReactNode => (
  <div className='bomdisoft-switcher-palette-failed' data-testid={testId}>
    <p className='bomdisoft-switcher-palette-failed-top'>
      {message}
      <button className='bomdisoft-switcher-palette-retry' data-testid={retryTestId} onClick={onRetry} type='button'>
        {retryLabel}
      </button>
    </p>
    {reason === undefined || reason === '' ? null : (
      <p className='bomdisoft-switcher-palette-failed-reason' data-testid={reasonTestId}>
        {reason}
      </p>
    )}
  </div>
);

export type SwitcherPaletteEmptyProps = { message: ReactNode; testId: string };

export const SwitcherPaletteEmpty = ({ message, testId }: SwitcherPaletteEmptyProps): ReactNode => (
  <p className='bomdisoft-switcher-palette-empty' data-testid={testId}>
    {message}
  </p>
);

const LockGlyph = (): ReactNode => (
  <svg aria-hidden='true' className='bomdisoft-switcher-palette-lock' fill='none' height='15' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='1.4' viewBox='0 0 16 16' width='15'>
    <rect height='6.5' rx='1.5' width='9' x='3.5' y='7' />
    <path d='M5.5 7V5a2.5 2.5 0 0 1 5 0v2' />
  </svg>
);
